<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal size="lg" :visible="modal.show" :data="model" :title="modalTitle"
                   @show="onShow" @hidden="onHidden" @ok.prevent="handleSubmit(onSubmit)"
                   :show-confirmation="showConfirmation" :ok-title="$t('Submit')" :is-loading="loading" scrollable>
      <div class="row">
        <div class="col-md-12">
          <div class="d-flex justify-content-center">
            <div class="col-md-8">
              <bidder-select-input-widget
                type="text"
                attribute="paddle_number"
                value-field="paddle_number"
                :model="model"
                :sale="modal.sale"
                is-search
                :auction-uuid="modal.sale ? modal.sale.uuid : null"
                @select="onBidderSelect"
              />
            </div>
          </div>

        </div>
      </div>

      <div class="row mb-4">
        <div class="col-md-12">
          <div v-if="bidder">
            <h5 class="mb-0">
              <router-link tabindex="-1"
                           :to="{ name: 'customers.dashboard', params: { id: bidder.customer.uuid }}">
                {{ bidder.customer.display_name }}
              </router-link>
            </h5>
            <p class="mb-0" v-if="bidder.customer && bidder.customer.billing_address"
               v-html="$options.filters.asAddress(bidder.customer.billing_address, false)">
            </p>
            <p class="m-0" v-if="bidder.customer.email">
              {{ bidder.customer.email }}
            </p>
            <p class="m-0" v-if="bidder.language">
              {{ $t("Language") }}: {{ bidder.language.name }}
            </p>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">

          <div class="card mb-3">
            <div class="card-header d-flex align-items-center justify-content-between">
              <div>
                {{ $t('Bids') }}
              </div>
              <b-button variant="link" class="p-0" @click="addNewBid" v-if="!hasArtisioTimedProvider">
                <font-awesome-icon :icon="'plus-circle'"/>
                {{ $t('New Bid') }}
              </b-button>
            </div>
            <div class="card-body">
              <div class="row" v-for="(bid, index) in bids" :key="`bid-row-${index}`"
                   :class="{'border-bottom mb-2': bids && index < bids.length - 1}">
                <div class="col-md-11">
                  <div class="row">
                    <div class="col-sm-12 col-md-6 col-lg-3">
                      <input-widget :model="bid" attribute="lot_no" :rules="{regex: LOT_NO_REGEX, required: true}"/>
                    </div>
                    <div class="col-sm-12 col-md-6 col-lg-3">
                      <currency-input-widget :rules="bid.type === 'commission' ? 'required' : ''"
                                             :model="bid" :currency="modal.sale.currency.symbol"
                                             attribute="amount"/>
                    </div>
                    <div class="col-sm-12 col-md-6 col-lg-3">
                      <input-widget :model="bid" attribute="status" value-field="value" display-field="${item.key}"
                                    type="select2" :items="statuses"/>
                    </div>
                    <div class="col-sm-12 col-md-6 col-lg-3">
                      <input-widget :model="bid" attribute="type" type="select2" value-field="value"
                                    @select2Select="onTypeSelect(bid)"
                                    display-field="${item.key}" :items="availableTypes"/>
                    </div>
                    <div class="col-sm-12 col-md-6 col-lg-3" v-if="bid.type !== 'max_bid'">
                      <clerk-select-input-widget :model="bid" attribute="clerk_uuid" :auction="modal.sale"/>
                    </div>
                    <div class="col-sm-12 col-md-6 col-lg-3">
                      <input-widget :model="bid" attribute="extra_step" rules="min_value:1|max_value:100"/>
                    </div>
                    <div class="col-sm-12 col-md-6 col-lg-3" v-if="bid.type !== 'max_bid'">
                      <input-widget :model="bid" attribute="phone" :rules="getPhoneRules(bid)"/>
                    </div>
                    <div class="col-sm-12 col-md-6 col-lg-3" v-if="bid.type !== 'max_bid'">
                      <input-widget :model="bid" attribute="phone2"/>
                    </div>
                    <div class="col-sm-12 col-md-6 col-lg-3">
                      <input-widget :model="bid" attribute="date" type="datepicker" :rules="bid.time ? 'required' : ''"/>
                    </div>
                    <div class="col-sm-12 col-md-6 col-lg-3">
                      <input-widget :model="bid" attribute="time" type="timepicker" :rules="bid.date ? 'required' : ''"/>
                    </div>
                    <div class="col-sm-12">
                      <input-widget :model="bid" type="textarea" attribute="description"/>
                    </div>
                  </div>
                </div>
                <div class="col-md-1 text-center" v-if="index > 0">
                  <b-tooltip :target="`remove-bid-button-${index}`" :title="$t('Delete')"/>
                  <font-awesome-icon class="text-danger hover-cursor" tabindex="-1" :id="`remove-bid-button-${index}`"
                                     @click="removeBidByIndex(index)" style="font-size: 1.5em;margin-top: .25em"
                                     :icon="'times'"/>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

    </artisio-modal>
  </ValidationObserver>
</template>

<script>

  import InputWidget from "../../../core/components/input-widget/InputWidget.vue";
  import ArtisioModal from "../../../core/components/artisio-modal/ArtisioModal.vue";
  import {createNamespacedHelpers} from "vuex";
  import PreBidsModal from "../models/PreBidsModal.model";
  import BidModel from "../models/Bid.model";
  import CurrencyInputWidget from "../../../core/widgets/CurrencyInputWidget.vue";
  import i18n from '../../../shared/i18n'
  import {getPhoneRuleObj, LOT_NO_REGEX} from "@/plugins/vee-validate";
  import ClerkSelectInputWidget from "@/core/widgets/ClerkSelectInputWidget.vue";
  import {Formatter} from "@/core/services/formatter.service";
  import BidderSelectInputWidget from "@/core/widgets/BidderSelectInputWidget.vue";

  const {mapState, mapActions} = createNamespacedHelpers('preBids');
  const {mapState: mapStateGlobal} = createNamespacedHelpers('global');

  export default {
    name: "PreBidsModal",
    components: {
      ClerkSelectInputWidget,
      CurrencyInputWidget,
      BidderSelectInputWidget,
      InputWidget,
      ArtisioModal
    },
    data() {
      return {
        loading: false,
        showConfirmation: true,
        model: new PreBidsModal(),
        bidder: null,
        bids: [],
        statuses: [
          {key: i18n.t('Pending'), value: 'pending'},
          {key: i18n.t('Confirmed'), value: 'confirmed'},
        ],
        LOT_NO_REGEX: LOT_NO_REGEX,
      }
    },
    computed: {
      ...mapState({
        modal: state => state.modal,
      }),
      ...mapStateGlobal({
        hasArtisioTimedProvider: state => state.hasArtisioTimedProvider,
      }),
      availableTypes() {
        if (this.modal && this.modal.sale && this.modal.sale.type === 'timed') {
          return [
            {value: 'max_bid', key: i18n.t('Max Bid')},
          ]
        }
        return [
          {value: 'commission', key: i18n.t('Commission')},
          {value: 'telephone', key: i18n.t('Telephone')},
        ]
      },
      modalTitle() {
        if (this.modal && this.modal.sale && this.modal.sale.type === 'timed') {
          return this.$i18n.t('Max Bids');
        }
        return this.$i18n.t("Commission & Telephone Bids");
      }
    },
    watch: {},
    methods: {
      ...mapActions(['hideModal', 'createPreBids']),
      onShow() {
        this.bidder = null;
        this.showConfirmation = true;
        this.addNewBid();
      },
      onHidden() {
        this.model = new PreBidsModal();
        this.bids = [];
        this.hideModal();
      },
      async onSubmit() {
        let requestData = [];
        this.bids.forEach((bid) => {
          let data = bid.toJSON()

          if (data.type === 'telephone') {
            data.amount = data.amount ? data.amount : 0
          }

          if (data.date) {
            data.date = Formatter.getDateAndTimeToSystemFormat(data.date, data.time ? data.time : Formatter.getNowAsTime())
          }

          if (data.extra_step === '') {
            data.extra_step = null
          }

          requestData.push({
            paddle_number: this.model.paddle_number,
            sale_no: this.modal.sale.sale_no,
            auction_uuid: this.modal.sale.uuid,
            ...data
          })
        });

        this.loading = true;
        let response = await this.createPreBids(requestData);
        this.loading = false;

        if (response.success) {
          this.showConfirmation = false;
          this.hideModal();
          this.$toast('success', this.$t('Bid(s) successfully created'));
        } else {
          if (response.toastError) {
            this.$errorDialog(response.toastError);
          } else {
            if (response.body.non_field_errors) {
              this.$errorDialog(response.body.non_field_errors[0]);
            } else if (response.body.length === 1 && response.body[0] && response.body[0].non_field_errors) {
              this.$errorDialog(response.body[0].non_field_errors[0]);
            } else {
              const non_field_errors = [];
              for (let index in response.body) {
                if (response.body[index] && response.body[index].non_field_errors) {
                  non_field_errors.push(response.body[index].non_field_errors[0])
                }
                this.bids[index].setMultipleErrors(response.body[index]);
              }
              if (non_field_errors.length > 0) {
                this.$errorDialog(
                  this.$createElement('ol', {class: ['list']},
                    non_field_errors.map(item => this.$createElement('li', {style: 'font-size: 16px; line-height: 23px;'}, item)))
                );
              }
            }
          }
        }
      },
      addNewBid() {
        let defaultData = {status: 'confirmed'}
        if (this.modal && this.modal.sale && this.modal.sale.type === 'timed') {
          defaultData.type = 'max_bid'
        } else {
          defaultData.type = 'commission'
        }

        this.bids.push(new BidModel(defaultData));
      },
      removeBidByIndex(index) {
        for (let i = 0; i < this.bids.length; i++) {
          if (i === index) {
            this.bids.splice(i, 1);
            break;
          }
        }
      },
      getPhoneRules(bid) {
        return getPhoneRuleObj(bid.type === 'telephone')
      },
      onBidderSelect(data) {
        this.bidder = data
        this.bids.forEach(b => this.assignPhoneToModel(b))
      },
      onTypeSelect(model) {
        this.assignPhoneToModel(model)
      },
      assignPhoneToModel(bidModel) {
        if (bidModel && bidModel.type === 'telephone' && this.bidder && this.bidder.customer && this.bidder.customer.phone) {
          let phone1 = null;
          let phone2 = null;
          let splitValue = this.bidder.customer.phone.split(',')

          if (splitValue && splitValue.length > 0) {
            phone1 = splitValue[0]
          }
          if (splitValue && splitValue.length > 1) {
            phone2 = splitValue[1]
          }

          bidModel.phone = phone1
          bidModel.phone2 = phone2
        }
      }
    }
  }
</script>

<style scoped>

</style>
